import {
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Typography,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { submitChangePassword } from '../../services/AuthenticationService'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch } from '../../app/hooks'
import { logOutUser } from '../../redux/authentication/AuthenticationSlice'
import ValidationHint from '../shared/atom/MpValidationHint'
import { ReactComponent as Visibility } from '@meprism/shared/src/assets/icons/visibility.svg'
import  { ReactComponent as VisibilityOff } from '@meprism/shared/src/assets/icons/visibilityoff.svg'

const styles = {
  heading: {
    color: '#3A155A',
    fontFamily: 'InterMedium',
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '27px',
    textTransform: 'uppercase',
  },
  label:{
    fontFamily: 'InterRegular',
    fontWeight: 400,
    fontSize: '14px',
    color: '#6B7280',
  },
  required:{
    '&::after': {
    content: '"*"',
    color: '#9135E0',
  },
  },
  textField: {
    width: '100%',
    backgroundColor: '#FFF',
    borderRadius: '10px',
    height: '50px',
    overflow: 'hidden',
    '& .MuiOutlinedInput-notchedOutline': {border: '1.5px solid #E2E8F0'},
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: '1.5px solid #9135E0'
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: '1.5px solid #9135E0'
    },
    '& .MuiInputBase-input::placeholder': {
      color: '#A0AEC0',
      opacity: 1,
      fontSize: '14px',
      fontWeight: 400,
      fontFamily: 'InterRegular',
    },
    '& .MuiInputBase-input': {
      color: '#3A155A',
      paddingLeft: '20px',
      fontFamily: 'InterRegular',
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: '140%',
    },
  },
  resetPassword: {
    width: {xs: '100%'},
    maxWidth: {xs:'152px', md: '200px'},
    height: 50,
    borderRadius: '10px',
    fontSize: {xs: '14px'}, 
    fontFamily: 'InterMedium',
    fontWeight: 500,
    background: '#9135E0',
    color: '#fff',
    '&.Mui-disabled': {
      background: '#9135E0',
      color: '#fff',
    },
    ':hover': {
      background: '#9135E0',
      color: '#fff',
    },
  },
  textDanger: {
    color: 'red',
  },
}

const ResetPassword = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const minPasswordLength = 10
  const [password, setPassword] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  })
  const [showPassword, setShowPassword] = useState({
    showOldPassword: false,
    showNewPassword: false,
    showConfirmPassword: false,
  })
  const { showOldPassword, showNewPassword, showConfirmPassword } = showPassword
  const { oldPassword, newPassword, confirmPassword } = password
  const [shouldDisplayPasswordInfo, setShouldDisplayPasswordInfo] =
    useState<boolean>(false)
  const [shouldShowMatchingPasswords, setShouldShowMatchingPasswords] =
    useState<boolean>(false)
  const [hasSufficientLength, setHasSufficientLength] = useState<boolean>(false)
  const [hasUpperCase, setHasUpperCase] = useState<boolean>(false)
  const [hasSymbol, setHasSymbol] = useState<boolean>(false)
  const [hasMatchingPassword, setHasMatchingPassword] = useState<boolean>(true)
  const [isErrResetPwd, setErrResetPwd] = useState<null | string>(null)
  const isPasswordValid =
    hasSufficientLength && hasUpperCase && hasSymbol && hasMatchingPassword

  useEffect(() => {
    setHasSufficientLength(newPassword.length >= minPasswordLength)
    setHasUpperCase(new RegExp(/[A-Z]/).test(newPassword))
    setHasSymbol(new RegExp(/[^A-Za-z0-9]/).test(newPassword))
    setHasMatchingPassword(newPassword === confirmPassword)
  }, [newPassword, confirmPassword])

  const handleResetPassword = async () => {
    try {
      setErrResetPwd(null)
      await submitChangePassword(oldPassword, newPassword)
      await dispatch(logOutUser()).unwrap()
      navigate('/login')
    } catch (error) {
      if (error.name === 'NotAuthorizedException')
        setErrResetPwd('Wrong old password')
      else if (error.name === 'PasswordHistoryPolicyViolationException')
        setErrResetPwd('You cannot reuse a previous password. Please choose a different password.')
      else setErrResetPwd('Could not reset password. Please try again later.')
    }
  }

  const handleSetPassword = (key: string, value: string) => {
    setPassword({ ...password, [key]: value })
  }

  const handleClickShowPassword = (key: string, value: boolean) => {
    setShowPassword({ ...showPassword, [key]: !value })
  }

  return (
    <Grid item xs={12}>
      <Typography sx={styles.heading}>Reset Password</Typography>
      <Box sx={{ marginBlock: 3 }}>
        <Typography sx={styles.label}>
          You will be logged out of your current session and will be required to
          log back in with your new password.
        </Typography>
      </Box>
      <Grid container rowSpacing={3.5} columnSpacing={3.5} alignItems={'flex-end'}>
        <Grid item xs={12} md={6} lg={3.4}>
          <Typography sx={{...styles.label, ...styles.required}}>Old Password</Typography>
          <FormControl variant="outlined" fullWidth>
            <OutlinedInput
              placeholder="Enter old password"
              sx={styles.textField}
              onChange={(e) => handleSetPassword('oldPassword', e.target.value)}
              type={showOldPassword ? 'text' : 'password'}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    sx={{ fontSize: '10px' }}
                    aria-label="toggle password visibility"
                    onClick={() =>
                      handleClickShowPassword(
                        'showOldPassword',
                        showOldPassword,
                      )
                    }
                    edge="end">
                    {showOldPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              }
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6} lg={3.4}>
          <Typography sx={{...styles.label, ...styles.required}}>New Password</Typography>
          <FormControl variant="outlined" fullWidth>
            <OutlinedInput
              placeholder='Enter new password'
              sx={styles.textField}
              onFocus={() => setShouldDisplayPasswordInfo(true)}
              onChange={(e) => handleSetPassword('newPassword', e.target.value)}
              type={showNewPassword ? 'text' : 'password'}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() =>
                      handleClickShowPassword(
                        'showNewPassword',
                        showNewPassword,
                      )
                    }
                    edge="end">
                    {showNewPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              }
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6} lg={3.4}>
          <Typography sx={{...styles.label, ...styles.required}}>Confirm New Password</Typography>
          <FormControl variant="outlined" fullWidth>
            <OutlinedInput
              placeholder='Enter again new password'
              sx={styles.textField}
              onChange={(e) =>
                handleSetPassword('confirmPassword', e.target.value)
              }
              onFocus={() => setShouldShowMatchingPasswords(true)}
              type={showConfirmPassword ? 'text' : 'password'}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() =>
                      handleClickShowPassword(
                        'showConfirmPassword',
                        showConfirmPassword,
                      )
                    }
                    edge="end">
                    {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              }
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6} lg={1.8} display={'flex'}>
          <Button
            disabled={!isPasswordValid}
            sx={styles.resetPassword}
            onClick={handleResetPassword}>
            Reset Password
          </Button>
        </Grid>
      </Grid>
      <Grid item md={12}>
          <Box>
            {isErrResetPwd && (
              <ValidationHint isValid={false} text={isErrResetPwd} />
            )}
            {shouldDisplayPasswordInfo && (
              <Box>
                <ValidationHint
                  isValid={hasSufficientLength}
                  text={`At least ${minPasswordLength} characters`}
                />
                <ValidationHint
                  isValid={hasUpperCase}
                  text={'Contains at least one upper case letter'}
                />
                <ValidationHint
                  isValid={hasSymbol}
                  text={'Contains number or symbol'}
                />
                {shouldShowMatchingPasswords && (
                  <ValidationHint
                    isValid={hasMatchingPassword}
                    text={'Passwords match'}
                  />
                )}
              </Box>
            )}
          </Box>
      </Grid>
    </Grid>
  )
}

export default ResetPassword
